import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Leave a multi-step flow and discard its store.
 *
 * React Router runs `navigate()` inside a transition, and the destination page
 * is usually lazy-loaded, so resetting the store *before* navigating re-renders
 * the current step first — and its guard (`<Navigate to=…pricing />`) sends the
 * user back into the flow. Instead, navigate now and reset once the component
 * that called this has unmounted, i.e. after the flow has actually been left.
 */
export function useLeaveFlow(reset: () => void) {
  const navigate = useNavigate();
  const leaving = useRef(false);

  useEffect(
    () => () => {
      if (leaving.current) reset();
    },
    [reset],
  );

  return useCallback(
    (to: string) => {
      leaving.current = true;
      navigate(to, { replace: true });
    },
    [navigate],
  );
}
